import { useRef, useState } from "react";

import { useField } from "formik";
import { isNotEmpty, findIndexById } from "neetocist";
import { append, assocPath, move, remove, prop, insert, clone } from "ramda";
import { useTranslation } from "react-i18next";

import { OPTION_ACTIONS } from "../constants";
import { filterDeletedRecords, generateLabel, randomId } from "../utils";

const attachPosition = options =>
  options.map((option, index) => ({ ...option, position: index }));

const useOptionFields = ({
  options,
  onChange,
  shouldDestroy,
  itemLabel,
  shouldResetEmptyOptionOnBlur,
  name,
  destroyFlagName,
  isNewItemsPrefilled,
  isAddOptionEnabled,
  isPictureChoice,
  isMultiInput,
  inputConfig,
}) => {
  const { t } = useTranslation();
  const [shouldFocus, setShouldFocus] = useState(false);
  const editingOptionDetailsRef = useRef({ id: "", value: "" });

  const [__, _, helpers] = useField(name);
  const setValue = helpers.setValue;

  const handleChange = ({
    id,
    value,
    inputIndex = 0,
    isOnBlur = false,
    fieldType = "label",
  }) => {
    const index = findIndexById(id, options);
    const currentOption = options[index];

    // Handle image updates for picture choice (no blur/reset logic needed)
    if (isPictureChoice && fieldType === "image") {
      const nextOptions = assocPath([index, "image"], value, options);
      setValue(nextOptions);
      onChange?.(nextOptions, OPTION_ACTIONS.UPDATE_IMAGE, index);

      return;
    }

    // Handle multi-input changes
    if (isMultiInput && typeof inputIndex === "number") {
      const currentValues = currentOption.values || [];
      const newValues = clone(currentValues);
      newValues[inputIndex] = value;

      const nextOptions = assocPath([index, "values"], newValues, options);
      setValue(nextOptions);
      onChange?.(nextOptions, OPTION_ACTIONS.UPDATE_VALUES, index);

      return;
    }

    // Common blur event handling - ignores onBlur if value is not cleared
    // The value will be reset to initial value if user clicks outside after clearing
    if (isOnBlur && (isNotEmpty(value) || !shouldResetEmptyOptionOnBlur)) {
      return;
    }

    if (editingOptionDetailsRef.current.id !== id) {
      editingOptionDetailsRef.current = {
        id,
        value: currentOption.label,
      };
    }

    const valueToUpdate = isOnBlur
      ? editingOptionDetailsRef.current.value
      : value;

    // Update options with the determined value (always updating label field)
    const nextOptions = assocPath([index, "label"], valueToUpdate, options);
    setValue(nextOptions);
    onChange?.(nextOptions, OPTION_ACTIONS.UPDATE_LABEL, index);
  };

  const handleDelete = (id, arrayHelpers) => {
    let nextOptions;

    const indexToDelete = findIndexById(id, options);
    if (shouldDestroy) {
      nextOptions = assocPath([indexToDelete, destroyFlagName], true, options);
      setValue(nextOptions);
    } else {
      arrayHelpers.remove(indexToDelete);
      nextOptions = attachPosition(remove(indexToDelete, 1, options));
    }

    onChange?.(nextOptions, OPTION_ACTIONS.DELETE, indexToDelete);
  };

  const handleDragEnd = ({ source, destination }) => {
    // When dragged outside of draggable
    if (destination === null) return;

    const deletedOptions = options.filter(prop(destroyFlagName));
    const filteredOptions = filterDeletedRecords(options, destroyFlagName);
    const nextOptions = move(source.index, destination.index, filteredOptions);
    const finalOptions = attachPosition(nextOptions);
    setValue([...deletedOptions, ...finalOptions]);
    onChange?.([...deletedOptions, ...finalOptions], OPTION_ACTIONS.REORDER);
  };

  const handleAddOption = () => {
    const id = randomId();
    const position = options.length;
    const newOption = {
      id: `draft-${id}`,
      altId: `alt-${id}`,
      label: isNewItemsPrefilled ? generateLabel(itemLabel, options) : "",
      position,
      ...(isPictureChoice && { image: null }),
      ...(isMultiInput && {
        values: inputConfig.map(config => config.defaultValue || ""),
      }),
    };

    setShouldFocus(true);

    const nextOptions = append(newOption, options);
    setValue(nextOptions);
    onChange?.(nextOptions, OPTION_ACTIONS.ADD, position);
  };

  const handleBulkAddOption = optionsString => {
    setShouldFocus(true);
    const position = options.length;
    const newOptions = optionsString
      .trim()
      .split(/\r?\n|,/)
      .filter(isNotEmpty)
      .map((option, index) => ({
        id: `draft-${randomId()}`,
        altId: `alt-${randomId()}`,
        label: option.trim(),
        position: position + index,
      }));

    const nextOptions = [...options, ...newOptions];
    setValue(nextOptions);
    onChange?.(nextOptions, OPTION_ACTIONS.BULK_ADD, position);
  };

  const handleClone = id => {
    const index = findIndexById(id, options);
    const optionToClone = options[index];

    if (!optionToClone) return;

    const newId = randomId();
    const clonedOption = {
      ...optionToClone,
      id: `draft-${newId}`,
      altId: `alt-${newId}`,
      label: isMultiInput
        ? optionToClone.label
        : t("neetoMolecules.optionFields.clonedOption", {
            originalLabel: optionToClone.label,
          }),
      ...(isMultiInput &&
        optionToClone.values && { values: clone(optionToClone.values) }),
    };

    setShouldFocus(true);

    const nextOptions = insert(index + 1, clonedOption, options);
    const optionsWithUpdatedPositions = attachPosition(nextOptions);
    setValue(optionsWithUpdatedPositions);
    onChange?.(optionsWithUpdatedPositions, OPTION_ACTIONS.CLONE, index + 1);
  };

  const handleKeyDown = event => {
    if (event.key !== "Enter") return;

    const form = event.target.form;
    const inputs = Array.from(form.querySelectorAll("input"));
    const currentInput = inputs.indexOf(event.target);

    if (currentInput >= inputs.length - 1) {
      isAddOptionEnabled && handleAddOption();
    } else {
      inputs[currentInput + 1]?.focus();
      event.preventDefault();
    }
    event.stopPropagation();
  };

  return {
    shouldFocus,
    handleAddOption,
    handleBulkAddOption,
    handleChange,
    handleKeyDown,
    handleDelete,
    handleDragEnd,
    handleClone,
  };
};

export default useOptionFields;
